/* ==========================================================================
   SNUG REBUILD — SIZE FINDER + SIZE-TABLE TOGGLE  (assets/rebuild-sizefinder.js)
   Owner: A5-pdp / r3:pdp.

   LOADING: SINGLE source of truth AND production runtime. Loaded as an EXTERNAL
   <script src> with `defer` from sections/product-template.liquid. The store's
   lazyload optimizer rewrites INLINE <script> to type=text/lazyload and never
   re-runs them — external src files load + execute normally, so all
   size-finder + size-table logic lives here.

   R3 UPGRADE (2026-06-15) — the finder used to return "öneri hazır değil" for
   any product without a parseable BODY-HEIGHT range. Owner feedback: it must
   ALWAYS return a real recommendation, and must take HEIGHT + WEIGHT + usual
   size (weighted). This rewrite:
     • adds a WEIGHT input (kg);
     • recommends through tiers and NEVER dead-ends:
         T1 height-range table  → exact body-height band (best, garment-verified)
         T1b + weight refine     → nudge ±1 size when weight is heavy/light for band
         T2 height/weight bands  → maps body metrics onto the product's OWN real
                                    size labels (S<M<L ...) — no cm invented
         T3 usual-size match     → maps the shopper's habitual size to a real label
         T4 median fallback      → middle of the product's real available sizes
     • never fabricates measurements — it only ever recommends one of the
       product's ACTUAL size labels (read from the size swatches / table).

   TRACKING SAFETY: to APPLY a recommendation it ONLY checks the existing size
   radio and dispatches a native 'change' event — exactly what the legacy
   product-template handler already does on swatch taps. It never calls
   /cart/add.js, never touches cart:updated / ajaxProduct:added.
   ========================================================================== */
(function () {
  'use strict';

  // ---- Touch-safe activation helper -----------------------------------------
  // REAL-DEVICE FIX (2026-06-15): on this theme a genuine finger tap fires
  // touchstart/touchend but the synthetic `click` that should follow is
  // SUPPRESSED (verified: pg.mouse.click() opens the panel, pg.touchscreen.tap()
  // does NOT — the click never reaches the button). So the fit-tool buttons
  // ("Beden tablosu" / "Bedenimi bul"), the "Bedenimi öner" button and the
  // "Bu bedeni seç" apply button were dead to touch even though click + the
  // delegated handler were correctly bound. Fix: bind BOTH `click` AND a
  // touch/pointer activation, de-duplicated so one tap never fires twice.
  //
  // Listener-bound dedupe: when a touch DOES synthesize a click, the click
  // arrives <700ms after the touchend on the same target — we swallow it.
  function onActivate(el, handler) {
    if (!el || el.__rbAct) return;
    el.__rbAct = true;
    var lastTouch = 0;
    var moved = false;
    // touchend is the primary touch path: calling preventDefault() on a
    // touchend that did NOT scroll cancels the synthetic mouse/click sequence
    // per spec, so the handler fires exactly once on a real tap. We still
    // ignore the event if the finger moved (a scroll, not a tap).
    el.addEventListener('touchstart', function () { moved = false; }, { passive: true });
    el.addEventListener('touchmove', function () { moved = true; }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (moved) return;                 // it was a scroll/drag, not a tap
      lastTouch = Date.now();
      if (e.cancelable) e.preventDefault(); // cancel the ghost click
      handler(e);
    });
    // click handles mouse / keyboard (Enter on a focused button) / any
    // environment where touchend didn't suppress the synthetic click.
    el.addEventListener('click', function (e) {
      if (Date.now() - lastTouch < 1000) return; // a recent tap already handled it
      handler(e);
    });
  }

  // ---- R5 placement fix -----------------------------------------------------
  // The trigger buttons (.rb-fit-tools--top) live near the title/price, but the
  // panels (.rb-sizetable / .rb-sizefinder) are rendered far lower in the flow
  // (below the description + swatches). So a click used to open a panel ~660px
  // BELOW the button instead of "exactly underneath" it. Relocate both panels to
  // sit immediately after the top trigger row so they inline-expand directly
  // under their buttons on desktop, and (via CSS) become a full-screen sheet on
  // mobile. We only MOVE the existing nodes — no markup is recreated, the size
  // finder still reads the size data from the section root, and no tracking /
  // cart markup is touched.
  function relocatePanels(scope) {
    var root = scope || document;
    var rows = root.querySelectorAll('.rb-fit-tools--top');
    rows.forEach(function (row) {
      if (row.__rbRelocated) return;
      var section = row.closest('[data-section-type="product"]') || row.closest('.shopify-section') || document;
      var host = document.createElement('div');
      host.className = 'rb-fit-panels';
      // insert the host right after the trigger row
      row.parentNode.insertBefore(host, row.nextSibling);
      ['rb-sizetable', 'rb-sizefinder'].forEach(function (cls) {
        var panel = section.querySelector('.' + cls);
        if (!panel) return;
        host.appendChild(panel);
        // Inject a sheet header (title + close) used only on the mobile sheet.
        // Hidden on desktop via CSS. Gives a real, tappable close affordance.
        var inner = panel.querySelector('.rb-sizetable__panel, .rb-sizefinder__panel');
        if (inner && !inner.querySelector('.rb-sheet-close')) {
          var label = cls === 'rb-sizetable' ? 'Beden tablosu' : 'Bedenimi bul';
          var bar = document.createElement('div');
          bar.className = 'rb-sheet-bar';
          bar.innerHTML = '<span class="rb-sheet-bar__title">' + label + '</span>' +
            '<button type="button" class="rb-sheet-close" data-rb-close="' + panel.id + '" aria-label="Kapat">&#10005;</button>';
          inner.insertBefore(bar, inner.firstChild);
        }
      });
      row.__rbRelocated = true;
    });
  }

  // Body-scroll lock for the mobile full-screen sheet. Only one panel open at a
  // time; reopening closes the other. On <=767px the open panel becomes a sheet.
  function isMobile() { return window.matchMedia('(max-width: 767px)').matches; }
  function lockBody(on) {
    document.documentElement.classList.toggle('rb-sheet-open', !!on);
    document.body.style.overflow = on ? 'hidden' : '';
  }
  function anySheetOpen() {
    return !!document.querySelector('.rb-sizetable.is-open, .rb-sizefinder.is-open');
  }
  function closeContainer(container) {
    if (!container) return;
    container.classList.remove('is-open');
    document.querySelectorAll('[data-rb-toggle="' + container.id + '"]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
  }

  // Delegated activation: a single document handler that resolves the nearest
  // [data-rb-toggle] on activation. Bound once globally for BOTH click and a
  // touch/pointer path (deduped) so real taps always toggle.
  function doToggle(t) {
    if (!t) return;
    var sel = t.getAttribute('data-rb-toggle');
    var container = document.getElementById(sel);
    if (!container) return;
    var willOpen = !container.classList.contains('is-open');
    // single-open: close the sibling panel first
    if (willOpen) {
      ['rb-sizetable', 'rb-sizefinder'].forEach(function (cls) {
        var other = document.querySelector('.' + cls + '.is-open');
        if (other && other !== container) closeContainer(other);
      });
    }
    container.classList.toggle('is-open', willOpen);
    // reflect aria on EVERY trigger that controls this panel (top + any in-flow)
    document.querySelectorAll('[data-rb-toggle="' + sel + '"]').forEach(function (b) {
      b.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
    if (isMobile()) lockBody(anySheetOpen());
    if (willOpen && isMobile()) { container.scrollTop = 0; }
  }
  if (!window.__rbSizeFinderBound) {
    window.__rbSizeFinderBound = true;
    var lastToggleTouch = 0;
    var tMoved = false;
    // Same touch-first, ghost-click-cancelling pattern as onActivate, but
    // delegated at the document so it covers BOTH trigger rows (the top
    // duplicate near the title AND the in-flow one) and survives re-renders.
    document.addEventListener('touchstart', function (e) {
      tMoved = false;
    }, { passive: true });
    document.addEventListener('touchmove', function () { tMoved = true; }, { passive: true });
    document.addEventListener('touchend', function (e) {
      if (tMoved) return;
      // CLOSE affordance (mobile sheet close button OR tap on the sheet backdrop)
      var c = e.target.closest && e.target.closest('[data-rb-close]');
      if (!c && e.target.classList && e.target.classList.contains('rb-sizetable')) c = e.target.querySelector('[data-rb-close]') || e.target;
      if (c) {
        var pid = c.getAttribute && c.getAttribute('data-rb-close');
        var cont = pid ? document.getElementById(pid) : (e.target.classList.contains('rb-sizetable') || e.target.classList.contains('rb-sizefinder') ? e.target : null);
        if (cont) { lastToggleTouch = Date.now(); if (e.cancelable) e.preventDefault(); closeContainer(cont); if (isMobile()) lockBody(anySheetOpen()); return; }
      }
      var t = e.target.closest && e.target.closest('[data-rb-toggle]');
      if (!t) return;
      lastToggleTouch = Date.now();
      if (e.cancelable) e.preventDefault(); // cancel the ghost click that would re-toggle
      doToggle(t);
    });
    document.addEventListener('click', function (e) {
      if (Date.now() - lastToggleTouch < 1000) return; // a recent tap already toggled
      var c = e.target.closest && e.target.closest('[data-rb-close]');
      // backdrop tap: the .rb-sizetable/.rb-sizefinder element IS the sheet backdrop on mobile
      var backdrop = (e.target.classList && (e.target.classList.contains('rb-sizetable') || e.target.classList.contains('rb-sizefinder'))) ? e.target : null;
      if (c || backdrop) {
        var pid = c ? c.getAttribute('data-rb-close') : backdrop.id;
        var cont = document.getElementById(pid) || backdrop;
        if (cont) { closeContainer(cont); if (isMobile()) lockBody(anySheetOpen()); return; }
      }
      var t = e.target.closest && e.target.closest('[data-rb-toggle]');
      doToggle(t);
    });
    // Escape closes the mobile sheet.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var open = document.querySelector('.rb-sizetable.is-open, .rb-sizefinder.is-open');
        if (open) { closeContainer(open); lockBody(false); }
      }
    });
  }

  // Canonical size ordering. Maps any product's real labels onto a 0..n axis so
  // body metrics can pick a position WITHOUT inventing measurements.
  var SIZE_ORDER = ['XXS', 'XS', 'XS/S', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', 'XL/XXL', 'XXL', '3XL', '4XL'];
  function sizeRank(label) {
    var n = String(label).toUpperCase().replace(/\s+/g, '').replace(/BEDEN|SIZE/g, '');
    var i = SIZE_ORDER.indexOf(n);
    if (i !== -1) return i;
    // numeric (e.g. 28, 30, 42) — keep its own scale, flagged numeric
    var num = n.match(/^(\d{2,3})$/);
    if (num) return 1000 + (+num[1]);
    return -1;
  }

  // Parse a BODY-HEIGHT range from text. The verified tables put body height in
  // "(X-Y cm arası kişiler için uygundur)" / boy phrasing. A bare "87-89 cm"
  // with no body-height context is GARMENT length -> ignored (kept honest).
  function parseHeight(text) {
    if (!text) return null;
    var s = String(text).replace(/ /g, ' ');
    // (a) prose: "Boy / Aralığı ... 150-185 cm"
    // 10-04 size-finder fix: only 120-230 cm ranges are body height (87-89 cm = trouser length)
    function ok(x, y) { return x >= 120 && y <= 230 && x < y; }
    var bb = s.match(/(?:boy|aral[ıi][ğg][ıi])[^0-9]{0,20}(\d{2,3})\s*[-–—]\s*(\d{2,3})\s*cm/i);
    if (bb && ok(+bb[1], +bb[2])) return { min: +bb[1], max: +bb[2] };
    // (b) range tied to a height-context word (kişiler / uygun / boy)
    var re = /(\d{2,3})\s*[-–—]\s*(\d{2,3})\s*cm[^.;]*?(?:aras[ıi]\s*ki[şs]iler|ki[şs]iler\s*i[çc]in|uygun|boy|aras)/gi, m;
    while ((m = re.exec(s))) { if (ok(+m[1], +m[2])) return { min: +m[1], max: +m[2] }; re.lastIndex = m.index + 1; }
    // (c) single bound ("175 cm ve üzeri ... kişiler")
    var up = s.match(/(\d{3})\s*cm[^.;]*?(?:üzer|ve üzeri)[^.;]*?(?:ki[şs]iler|uygun|boy)/i);
    if (up && +up[1] >= 120) return { min: +up[1], max: 999 };
    return null;
  }

  // Pull the product's REAL size labels from the size swatches (truth) so the
  // finder can always recommend an actual selectable size.
  function realSizesFromForm(root) {
    var out = [];
    var labels = root.querySelectorAll('.swatches.sizes .size-swatch, label.size-swatch');
    labels.forEach(function (l) {
      var txt = (l.textContent || '').trim();
      if (txt && out.indexOf(txt) === -1) out.push(txt);
    });
    return out;
  }

  function extractSizeMap(panelEl) {
    var out = [];
    if (!panelEl) return out;
    var rows = panelEl.querySelectorAll('table tbody tr, table tr');
    rows.forEach(function (tr) {
      var cells = tr.querySelectorAll('td');
      if (!cells.length) return;
      var sizeLabel = (cells[0].textContent || '').trim();
      if (!sizeLabel) return;
      if (sizeLabel.length > 12 || /\s{1}\S+\s+\S+\s/.test(sizeLabel)) return; // 10-04: skip non-size rows (marketing text)
      var h = null;
      for (var i = 0; i < cells.length; i++) { h = parseHeight(cells[i].textContent); if (h) break; }
      if (!h) h = parseHeight(tr.textContent);
      out.push({ size: sizeLabel, h: h });
    });
    return out;
  }

  function findRadioForSize(root, sizeLabel) {
    if (!sizeLabel) return null;
    var norm = function (s) { return String(s).toLowerCase().replace(/\s+/g, '').replace(/[.\-]/g, ''); };
    var target = norm(sizeLabel);
    var labels = root.querySelectorAll('.swatches.sizes .size-swatch, label.size-swatch');
    for (var i = 0; i < labels.length; i++) {
      if (norm(labels[i].textContent) === target) {
        var id = labels[i].getAttribute('for');
        if (id) return root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id));
      }
    }
    return null;
  }

  // ---- weight nudging ------------------------------------------------------
  // Expected weight (kg) for the MIDDLE of a height band, roughly BMI ~22.5.
  // Used only to nudge ±1 real size — never to invent measurements.
  function expectedWeightForHeight(cm) {
    if (!cm) return null;
    var m = cm / 100;
    return 22.5 * m * m; // ~70kg @178cm
  }

  // Given the ordered list of the product's REAL sizes + a base index, nudge by
  // weight. Heavier-than-expected -> larger size; lighter -> smaller.
  function weightNudge(baseIdx, count, height, weight) {
    if (!weight || !height) return baseIdx;
    var exp = expectedWeightForHeight(height);
    if (!exp) return baseIdx;
    var diff = weight - exp;            // kg above/below the band's expected weight
    var step = 0;
    if (diff >= 12) step = 1;           // notably heavier -> up one size
    else if (diff <= -12) step = -1;    // notably lighter -> down one size
    var idx = baseIdx + step;
    if (idx < 0) idx = 0;
    if (idx > count - 1) idx = count - 1;
    return idx;
  }

  // Map height (and weight) to a position within the product's REAL ordered
  // size list when there is NO body-height table. Conservative, label-only.
  function heightToIndex(orderedSizes, height, weight) {
    var n = orderedSizes.length;
    if (n === 0) return -1;
    if (n === 1) return 0;
    // Height bands -> fractional position 0..1 across the available sizes.
    // SNUG pants run S/M (≤174) and L/XL (≥175); generalised across n sizes.
    var frac;
    if (height <= 158) frac = 0.08;
    else if (height <= 166) frac = 0.28;
    else if (height <= 173) frac = 0.46;
    else if (height <= 180) frac = 0.64;
    else if (height <= 188) frac = 0.82;
    else frac = 0.95;
    var idx = Math.round(frac * (n - 1));
    idx = weightNudge(idx, n, height, weight);
    return idx;
  }

  function orderSizes(sizes) {
    return sizes.slice().sort(function (a, b) {
      var ra = sizeRank(a), rb = sizeRank(b);
      if (ra === -1 && rb === -1) return 0;
      if (ra === -1) return 1;
      if (rb === -1) return -1;
      return ra - rb;
    });
  }

  // Core recommender — ALWAYS returns a {size, basis} object (never null) as
  // long as the product has at least one real size label.
  function recommend(map, realSizes, height, weight, usual) {
    var ordered = orderSizes(realSizes.length ? realSizes : map.map(function (r) { return r.size; }));
    if (!ordered.length) return null; // truly no sizes (should not happen for pants)

    // ---- T1: body-height table (best, garment-verified) ----
    var withH = map.filter(function (r) { return r.h; });
    if (height && withH.length) {
      var exact = withH.filter(function (r) { return height >= r.h.min && height <= r.h.max; });
      var picked = null, basis = 'height';
      if (exact.length) {
        picked = exact[0].size;
      } else {
        // nearest band by edge distance
        var nearest = withH.slice().sort(function (a, b) {
          var da = Math.min(Math.abs(height - a.h.min), Math.abs(height - a.h.max));
          var db = Math.min(Math.abs(height - b.h.min), Math.abs(height - b.h.max));
          return da - db;
        })[0];
        picked = nearest.size; basis = 'height-near';
      }
      // weight refine within the product's real ordered sizes
      var bIdx = ordered.indexOf(picked);
      if (bIdx === -1) bIdx = ordered.map(function (s) { return s.toUpperCase().replace(/\s+/g, ''); })
                                     .indexOf(String(picked).toUpperCase().replace(/\s+/g, ''));
      if (bIdx !== -1 && weight) {
        var nIdx = weightNudge(bIdx, ordered.length, height, weight);
        if (nIdx !== bIdx) return { size: ordered[nIdx], basis: 'height-weight' };
      }
      return { size: picked, basis: basis };
    }

    // ---- T2: height (+weight) mapped to the product's REAL size labels ----
    if (height) {
      var idx = heightToIndex(ordered, height, weight);
      if (idx !== -1) return { size: ordered[idx], basis: weight ? 'body' : 'body-height' };
    }

    // ---- T3: habitual size ----
    if (usual) {
      var u = String(usual).toUpperCase().replace(/\s+/g, '');
      // exact label match first
      for (var i = 0; i < ordered.length; i++) {
        if (ordered[i].toUpperCase().replace(/\s+/g, '') === u) return { size: ordered[i], basis: 'usual' };
      }
      // combined-size label that CONTAINS the usual size (e.g. usual M -> S/M)
      for (var j = 0; j < ordered.length; j++) {
        if (ordered[j].toUpperCase().indexOf(u) !== -1) return { size: ordered[j], basis: 'usual' };
      }
      // rank-nearest
      var ur = sizeRank(usual);
      if (ur !== -1) {
        var best = ordered.slice().sort(function (a, b) {
          return Math.abs(sizeRank(a) - ur) - Math.abs(sizeRank(b) - ur);
        })[0];
        if (best) return { size: best, basis: 'usual-near' };
      }
    }

    // ---- T4: median fallback (still a REAL size) ----
    return { size: ordered[Math.floor((ordered.length - 1) / 2)], basis: 'median' };
  }

  function init(widget) {
    if (widget.__rbInit) return;
    widget.__rbInit = true;
    var root = widget.closest('[data-section-type="product"]') || widget.closest('.shopify-section') || document;
    var sizeTablePanel = root.querySelector('[data-rb-sizetable-source]');
    var map = extractSizeMap(sizeTablePanel);
    var realSizes = realSizesFromForm(root);
    var hEl = widget.querySelector('[data-rb-height]');
    var wEl = widget.querySelector('[data-rb-weight]');
    var uEl = widget.querySelector('[data-rb-usual]');
    var goBtn = widget.querySelector('[data-rb-go]');
    var resultEl = widget.querySelector('[data-rb-result]');

    function render(rec) {
      if (!resultEl) return;
      resultEl.classList.add('is-shown');
      if (!rec) {
        // Only reached if the product literally has no size options.
        resultEl.innerHTML = '<p class="rb-sizefinder__hint">Bu ürün tek beden. Yine de emin değilsen <a href="https://wa.me/905059580054" data-rb-contact>bize yaz</a> — birlikte netleştirelim.</p>';
        return;
      }
      var copy;
      switch (rec.basis) {
        case 'height':       copy = 'Boyuna göre önerimiz — tablodaki ölçülere birebir uyuyor.'; break;
        case 'height-weight':copy = 'Boy ve kilona göre önerimiz. Tablodaki ölçülerle uyumlu.'; break;
        case 'height-near':  copy = 'Boyuna en yakın beden. Salaş bir kalıp seviyorsan bir üst bedeni de düşünebilirsin.'; break;
        case 'body':         copy = 'Boy ve kilona göre önerimiz. Bu kalıp salaş durur; daha toparlı istersen bir alt beden.'; break;
        case 'body-height':  copy = 'Boyuna göre önerimiz. Kilonu da girersen daha isabetli ayarlarız.'; break;
        case 'usual':        copy = 'Her zamanki bedenine göre önerimiz.'; break;
        case 'usual-near':   copy = 'Her zamanki bedenine en yakın seçenek.'; break;
        default:             copy = 'Bu ürün için en çok tercih edilen orta beden. Boy/kilo girersen kişiselleştirelim.';
      }
      resultEl.innerHTML =
        '<div class="rb-sizefinder__rec"><span class="rb-sizefinder__rec-size">' + rec.size + '</span>' +
        '<span class="rb-sizefinder__rec-copy"><strong>Önerilen beden</strong><br>' + copy + '</span></div>' +
        '<button type="button" class="rb-btn rb-btn--block rb-sizefinder__apply" data-rb-apply="' + encodeURIComponent(rec.size) + '">Bu bedeni seç</button>' +
        '<p class="rb-sizefinder__hint">Öneri; ürünün gerçek ölçülerine ve beden verisine dayanır, garanti değildir.</p>';
      var applyBtn = resultEl.querySelector('[data-rb-apply]');
      if (applyBtn) onActivate(applyBtn, function () {
        var sz = decodeURIComponent(applyBtn.getAttribute('data-rb-apply'));
        var radio = findRadioForSize(root, sz);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          applyBtn.textContent = sz + ' seçildi ✓';
          applyBtn.disabled = true;
        } else {
          applyBtn.textContent = 'Bu beden seçenekte yok';
          applyBtn.disabled = true;
        }
      });
    }

    if (goBtn) onActivate(goBtn, function () {
      var height = hEl && hEl.value ? parseInt(hEl.value, 10) : 0;
      var weight = wEl && wEl.value ? parseInt(wEl.value, 10) : 0;
      var usual = uEl && uEl.value ? uEl.value : '';
      if (!height && !weight && !usual) {
        if (resultEl) {
          resultEl.classList.add('is-shown');
          resultEl.innerHTML = '<p class="rb-sizefinder__hint">Boyunu (ve istersen kilonu) gir ya da her zamanki bedenini seç.</p>';
        }
        return;
      }
      render(recommend(map, realSizes, height, weight, usual));
    });
  }

  function boot() {
    relocatePanels(document); // R5: move panels under the top trigger row first
    var widgets = document.querySelectorAll('[data-rb-sizefinder]');
    for (var i = 0; i < widgets.length; i++) init(widgets[i]);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
